import { SHEET_PANEL_DURATION_MS, SHEET_PANEL_EASING } from '~/composables/useSheetNavigation';

/**
 * Animates the sheet's viewport height while panels slide horizontally.
 *
 * Without this, a push between panels of different heights makes the sheet snap
 * to its new size in one frame while the content is still travelling, which
 * reads as a glitch rather than as navigation.
 *
 * This is the one deliberate exception to "never animate layout properties"
 * (`.claude/rules/animations.md` §5): the animated subtree is the sheet's own
 * viewport, nothing outside it reflows, and no transform-based alternative can
 * resize a container without distorting its content. The horizontal travel
 * itself stays on `transform` / `opacity`, as the rule requires.
 *
 * Uses the Web Animations API rather than a CSS transition: it needs no
 * `transitionend` plumbing, reverts the property on its own when finished, and
 * a rapid second navigation simply cancels the first.
 */
export default function useSheetPanelHeight(viewport: Ref<HTMLElement | null>, current: Ref<string | null>) {
    let animation: Animation | null = null;

    const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Pre-flush watcher: the callback runs before Vue patches the DOM, which is
    // the only moment the outgoing panel's height is still the rendered one.
    watch(current, async (to, from) => {
        const element = viewport.value;
        if (!element || !from || !to || to === from) return;

        animation?.cancel();

        const fromHeight = element.offsetHeight;

        // A new panel starts at its top. Doing it here also keeps the outgoing
        // panel's `top-0` aligned with what the user sees while it slides away.
        element.scrollTop = 0;

        if (prefersReducedMotion()) return;

        await nextTick();

        // Read the viewport itself rather than the incoming panel. The height is
        // still `auto` at this point, so this is the height it will actually settle
        // at, already clamped by the sheet's max-height. The panel's own height
        // ignores that clamp: on a panel taller than the sheet it would animate
        // towards a value the layout can never reach, and the visible growth would
        // finish early and then sit still. The outgoing panel is positioned
        // absolutely, so it does not contribute to this box.
        const toHeight = element.offsetHeight;
        if (!toHeight || toHeight === fromHeight) return;

        animation = element.animate([{ height: `${fromHeight}px` }, { height: `${toHeight}px` }], {
            duration: SHEET_PANEL_DURATION_MS,
            easing: SHEET_PANEL_EASING,
        });

        // `cancel()` on a superseded animation rejects this promise.
        animation.finished.then(() => (animation = null)).catch(() => {});
    });

    onUnmounted(() => animation?.cancel());
}
